import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import API_URL from '@/config/api';
import { GETTING_STARTED, STAFF_SECTIONS } from '@/data/manual/staffManual';
import { tileMatches } from '@/components/help/manualTheme';

const SPECIAL = { profit_centre: 'canViewProfit', invoice_centre: 'canManageInvoices' };
// Custom-section icon keys (SectionStyleDialog) → manual icon names.
const CUSTOM_ICON = { folder: 'Folder', wallet: 'Wallet', wrench: 'Wrench', graduation: 'GraduationCap', package: 'Package', users: 'Users', truck: 'Truck', megaphone: 'Megaphone', chart: 'BarChart3', settings: 'Settings', star: 'Star', hammer: 'Hammer', paintbrush: 'Paintbrush', clipboard: 'ClipboardList', bell: 'Bell', landmark: 'Landmark' };

const orderBy = (items, saved, key) => {
  if (!saved?.length) return items;
  const rank = (x) => { const i = saved.indexOf(key(x)); return i < 0 ? 999 : i; };
  return [...items].map((x, i) => [x, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map((x) => x[0]);
};

const useLiveLayout = () => {
  const [layout, setLayout] = useState({ sections: [], titles: {}, tile_sections: {}, descriptions: {} });
  const [prefs, setPrefs] = useState({ section_order: [], tile_order: {} });
  useEffect(() => {
    const get = (p) => fetch(`${API_URL}/api/staff/${p}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    get('dashboard-layout').then((d) => d && setLayout(d));
    get('dashboard-favourites').then((d) => d && setPrefs({ section_order: d.section_order || [], tile_order: d.tile_order || {} }));
  }, []);
  return { layout, prefs };
};

// Role-aware manual that mirrors the live Staff Dashboard: renamed/custom sections, moved tiles and the reader's own order.
export default function useVisibleManual(query) {
  const auth = useAuth();
  const { user, can } = auth;
  const role = user?.role;
  const { layout, prefs } = useLiveLayout();

  return useMemo(() => {
    const allowedTile = (t) => {
      if (t.roles && !t.roles.includes(role) && !(user?.super_admin && t.roles.includes('admin'))) return false;
      if (t.feature && SPECIAL[t.feature]) return !!auth[SPECIAL[t.feature]];
      return !t.feature || can(t.feature);
    };
    const builtIns = STAFF_SECTIONS.filter((s) => !s.roles || s.roles.includes(role)).map((s) => ({ ...s, tiles: [] }));
    const customs = (layout.sections || []).map((c) => ({ id: c.id, panel: c.id, title: c.title, icon: CUSTOM_ICON[c.icon] || 'Folder', colour: c.colour || 'slate', intro: layout.descriptions?.[c.id] || '', tiles: [], custom: true }));
    const all = [...builtIns, ...customs];
    const byPanel = Object.fromEntries(all.map((s) => [s.panel, s]));
    const moved = layout.tile_sections || {};

    STAFF_SECTIONS.forEach((s) => s.tiles.forEach((t) => {
      if (!allowedTile(t) || !tileMatches(t, query)) return;
      const target = (t.tileIds || []).map((id) => moved[id]).find((p) => p && byPanel[p] && p !== s.panel);
      (byPanel[target] || byPanel[s.panel])?.tiles.push(t);
    }));

    const styled = all.map((s) => {
      const title = layout.titles?.[s.panel] || s.title;
      const desc = layout.descriptions?.[s.panel];
      const tiles = orderBy(s.tiles, prefs.tile_order?.[s.panel], (t) => (t.tileIds || [])[0]);
      return { section: { ...s, title, intro: desc !== undefined && !s.custom ? desc : s.intro }, tiles };
    });
    const ordered = orderBy(styled, prefs.section_order, (x) => x.section.panel).filter((x) => x.tiles.length > 0);
    const start = GETTING_STARTED.tiles.filter((t) => tileMatches(t, query));
    const sections = [...(start.length ? [{ section: GETTING_STARTED, tiles: start }] : []), ...ordered];
    const groups = new Set(['Admin', ...STAFF_SECTIONS.filter((s) => ordered.some((x) => x.section.id === s.id || x.tiles.some((t) => s.tiles.includes(t)))).map((s) => s.title)]);
    return { sections, groups, layoutApplied: Object.keys(moved).length > 0 || customs.length > 0 || Object.keys(layout.titles || {}).length > 0 || prefs.section_order.length > 0 };
  }, [auth, can, role, user, query, layout, prefs]);
}
